import React, { useState, useEffect } from "react";
import axios from "axios";
import { C, LAYOUT } from "../constants";
import PageHeader from "../components/PageHeader";
import Card from "../components/Card";
import Btn from "../components/Btn";
import Badge from "../components/Badge";
import { getPlannerContext, plannerContextBanner } from "../utils/plannerContext";
import { getApiBase } from "../apiBase";
import { formatApiError } from "../utils/apiError";

const BASE = getApiBase();
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const DIFF_COLORS = {
  easy: C.success,
  medium: C.warning,
  hard: C.error,
  mixed: C.primary,
};

const QUICK_COMPANIES = ["TCS", "Infosys", "Wipro", "Amazon", "Microsoft", "Google", "Zoho", "Accenture"];

const FALLBACK_COMPANIES = [
  { name: "TCS", category: "IT Services" },
  { name: "Infosys", category: "IT Services" },
  { name: "Wipro", category: "IT Services" },
  { name: "Cognizant", category: "IT Services" },
  { name: "Accenture", category: "IT Services" },
  { name: "Capgemini", category: "IT Services" },
  { name: "HCLTech", category: "IT Services" },
  { name: "Tech Mahindra", category: "IT Services" },
  { name: "Amazon", category: "Product / Tech" },
  { name: "Microsoft", category: "Product / Tech" },
  { name: "Google", category: "Product / Tech" },
  { name: "Adobe", category: "Product / Tech" },
  { name: "IBM", category: "Product / Tech" },
  { name: "Oracle", category: "Product / Tech" },
  { name: "Salesforce", category: "Product / Tech" },
  { name: "Zoho", category: "Product / Tech" },
  { name: "Freshworks", category: "Product / Tech" },
  { name: "Flipkart", category: "Product / Tech" },
  { name: "Goldman Sachs", category: "Finance / Analytics" },
  { name: "JP Morgan", category: "Finance / Analytics" },
  { name: "Deloitte", category: "Finance / Analytics" },
  { name: "EY", category: "Finance / Analytics" },
  { name: "Razorpay", category: "Fintech / Startup" },
  { name: "Zomato", category: "Fintech / Startup" },
  { name: "Swiggy", category: "Fintech / Startup" },
  { name: "Byju's", category: "Fintech / Startup" },
];

const fieldStyle = {
  width: "100%",
  padding: "10px 12px",
  border: `1.5px solid ${C.border}`,
  borderRadius: LAYOUT.radiusSm,
  fontSize: 14,
  color: C.text,
  background: C.surface,
  boxSizing: "border-box",
  fontFamily: "inherit",
};

const labelStyle = {
  display: "block",
  fontSize: 12,
  fontWeight: 700,
  color: C.muted,
  marginBottom: 8,
};

function SectionTitle({ icon, title, subtitle }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: C.primarySoft, border: `1px solid ${C.primary}22`,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: C.primary, fontSize: 16, fontWeight: 800, flexShrink: 0,
      }}>
        {icon}
      </div>
      <div>
        <p style={{ margin: 0, fontWeight: 800, fontSize: 14, color: C.text }}>{title}</p>
        {subtitle && (
          <p style={{ margin: "3px 0 0", fontSize: 12, color: C.muted, lineHeight: 1.4 }}>{subtitle}</p>
        )}
      </div>
    </div>
  );
}

function MCQCard({ q, i, showAnswers }) {
  return (
    <div style={{
      padding: "14px 12px",
      marginBottom: 8,
      borderRadius: LAYOUT.radiusSm,
      background: i % 2 ? C.surfaceAlt : C.surface,
      border: `1px solid ${C.border}`,
    }}>
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 10 }}>
        <span style={{
          minWidth: 28, height: 28, borderRadius: 8,
          background: C.primarySoft, color: C.primaryDark,
          fontWeight: 800, fontSize: 12,
          display: "inline-flex", alignItems: "center", justifyContent: "center",
        }}>{i + 1}</span>
        <div style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: C.text, lineHeight: 1.5 }}>
          {q.question}
        </div>
        {q.difficulty && (
          <Badge bg={`${DIFF_COLORS[q.difficulty] || C.muted}18`} color={DIFF_COLORS[q.difficulty] || C.muted}>
            {q.difficulty}
          </Badge>
        )}
      </div>
      <div style={{ paddingLeft: 38, display: "flex", flexDirection: "column", gap: 5 }}>
        {(q.options || []).map((opt, j) => {
          const highlight = showAnswers && j === q.correct;
          return (
            <div key={j} style={{
              padding: "8px 12px",
              borderRadius: LAYOUT.radiusSm,
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: highlight ? C.successBg : C.bg,
              color: highlight ? C.success : C.text,
              fontWeight: highlight ? 700 : 400,
              border: `1px solid ${highlight ? `${C.success}40` : C.border}`,
            }}>
              <span style={{
                minWidth: 22, height: 22, borderRadius: 6,
                background: highlight ? C.success : C.border,
                color: highlight ? "#fff" : C.muted,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>{["A", "B", "C", "D"][j]}</span>
              <span style={{ flex: 1 }}>{opt}</span>
              {highlight && <span style={{ color: C.success, fontWeight: 800 }}>✓</span>}
            </div>
          );
        })}
        {showAnswers && q.explanation && (
          <div style={{
            marginTop: 4, fontSize: 12.5, color: C.text, lineHeight: 1.55,
            padding: "10px 12px", background: C.warningBg, borderRadius: LAYOUT.radiusSm,
            borderLeft: `3px solid ${C.warning}`,
          }}>
            <strong style={{ color: C.warning }}>Explanation: </strong>
            <span style={{ color: C.muted }}>{q.explanation}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function Collapsible({ title, count, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card
      style={{ padding: 0, marginBottom: 12, borderLeft: `4px solid ${open ? C.primary : C.border}` }}
      sx={{ p: "0 !important", overflow: "hidden" }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%",
          padding: "14px 16px",
          cursor: "pointer",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: open ? C.primarySoft : C.surface,
          border: "none",
          borderBottom: open ? `1px solid ${C.border}` : "none",
          fontFamily: "inherit",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 800, fontSize: 14, color: C.text }}>{title}</span>
          <Badge bg={C.tagBg} color={C.tagText}>{count} Q</Badge>
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.primary }}>{open ? "Hide" : "Show"}</span>
      </button>
      {open && <div style={{ padding: "12px 14px 14px" }}>{children}</div>}
    </Card>
  );
}

function PatternPanel({ pattern, isLive }) {
  if (!pattern) return null;
  return (
    <div style={{
      background: isLive
        ? "linear-gradient(135deg, #E6F8F1 0%, #E8F0FF 100%)"
        : "linear-gradient(135deg, #EAF0FF 0%, #E0EBFF 100%)",
      borderRadius: LAYOUT.radius,
      padding: "14px 16px",
      border: `1px solid ${isLive ? `${C.success}44` : `${C.warning}55`}`,
      marginBottom: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
        <Badge bg={isLive ? C.successBg : C.warningBg} color={isLive ? C.success : C.warning}>
          {isLive ? "Live pattern" : "Offline pattern"}
        </Badge>
        {pattern.difficulty && (
          <Badge bg={C.tagBg} color={C.tagText}>{pattern.difficulty}</Badge>
        )}
      </div>
      {pattern.rounds?.length > 0 && (
        <div style={{ fontSize: 13, color: C.text, marginBottom: 4, fontWeight: 600 }}>
          Rounds: <span style={{ fontWeight: 500 }}>{pattern.rounds.join(" → ")}</span>
        </div>
      )}
      {pattern.notes && (
        <div style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>{pattern.notes}</div>
      )}
    </div>
  );
}

function downloadBank(bank) {
  const lines = [];
  lines.push(`PLACEMENT PREP — ${bank.company} (${bank.level})`);
  lines.push("=".repeat(60));
  (bank.sections || []).forEach((sec) => {
    lines.push(`\n━━━ ${sec.name.toUpperCase()} ━━━`);
    (sec.mcq || []).forEach((q, i) => {
      lines.push(`\nQ${i + 1}. [${q.difficulty?.toUpperCase()}] ${q.question}`);
      (q.options || []).forEach((o, j) => lines.push(`  ${["A", "B", "C", "D"][j]}. ${o}`));
      lines.push(`  Answer: ${["A", "B", "C", "D"][q.correct]}   Explanation: ${q.explanation || ""}`);
    });
  });
  const blob = new Blob([lines.join("\n")], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Placement_${bank.company}.txt`.replace(/\s+/g, "_");
  a.click();
}

function totalQCount(bank) {
  return (bank?.sections || []).reduce((sum, s) => sum + (s.mcq?.length || 0), 0);
}

export default function PlacementPrep() {
  const plannerCtx = getPlannerContext();
  const [tab, setTab] = useState("generate");
  const [companies, setCompanies] = useState(FALLBACK_COMPANIES);
  const [company, setCompany] = useState(
    plannerCtx.company || FALLBACK_COMPANIES[0].name
  );
  const [customCompany, setCustomCompany] = useState(
    plannerCtx.company && !QUICK_COMPANIES.includes(plannerCtx.company)
      ? plannerCtx.company
      : ""
  );
  const [useCustom, setUseCustom] = useState(
    Boolean(plannerCtx.company && !QUICK_COMPANIES.includes(plannerCtx.company))
  );
  const [level, setLevel] = useState("mixed");
  const [perSection, setPerSection] = useState(8);
  const [refreshPattern, setRefreshPattern] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [showAnswers, setShowAnswers] = useState(false);
  const [saved, setSaved] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [selectedBank, setSelectedBank] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${BASE}/placement/companies`, { headers: authH() });
        const apiCompanies = r.data.data || [];
        if (apiCompanies.length) {
          const byName = new Map(FALLBACK_COMPANIES.map((c) => [c.name.toLowerCase(), c]));
          apiCompanies.forEach((c) => byName.set(c.name.toLowerCase(), c));
          setCompanies([...byName.values()]);
          // Prefer planner dream company; only default to first API company if none set
          if (!plannerCtx.company) setCompany(apiCompanies[0].name);
        }
      } catch { /* keep fallback */ }
    })();
  }, []);

  useEffect(() => {
    if (tab === "saved") fetchSaved();
  }, [tab]);

  async function fetchSaved() {
    setSavedLoading(true);
    try {
      const r = await axios.get(`${BASE}/placement`, { headers: authH() });
      setSaved(r.data.data || []);
    } catch {
      setSaved([]);
    }
    setSavedLoading(false);
  }

  const activeCompany = useCustom ? customCompany.trim() : company;

  async function handleGenerate() {
    if (!activeCompany) {
      setError("Pick a company or type a custom name.");
      return;
    }
    setLoading(true);
    setError("");
    setResult(null);
    setMeta(null);
    try {
      const r = await axios.post(`${BASE}/placement/generate`, {
        company: activeCompany,
        level,
        questionsPerSection: perSection,
        refreshPattern,
        // Connect to Schedule Planner first inputs
        role: plannerCtx.role || "",
        problemHint: plannerCtx.problem || "",
        dsaHint: plannerCtx.dsaSyllabus || "",
      }, { headers: authH() });
      setResult(r.data.data);
      setMeta(r.data.meta);
      setShowAnswers(false);
    } catch (e) {
      setError(formatApiError(e, "Request failed"));
    }
    setLoading(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this placement bank?")) return;
    try {
      await axios.delete(`${BASE}/placement/${id}`, { headers: authH() });
      setSaved((s) => s.filter((b) => b._id !== id));
      if (selectedBank?._id === id) setSelectedBank(null);
    } catch { /* ignore */ }
  }

  const pickQuick = (name) => {
    setUseCustom(false);
    setCompany(name);
  };

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 20px 48px" }}>
      <style>{`
        @keyframes ppSpin { to { transform: rotate(360deg); } }
        @keyframes ppFade { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
      `}</style>

      <PageHeader
        title="Placement Prep"
        subtitle="Generate company-style practice sets — aptitude & technical MCQs with answers."
        icon={<i className="ti ti-briefcase" style={{ fontSize: 18 }} />}
        banner="Tip: pick a popular company below, set difficulty, then generate. Show answers when you want to check."
      />

      <div style={{
        marginBottom: 14, padding: "10px 14px", borderRadius: 10,
        background: C.primarySoft, border: `1px solid ${C.primary}22`,
        fontSize: 12.5, color: C.primaryDark, fontWeight: 600,
      }}>
        {plannerContextBanner(plannerCtx)}
      </div>

      {/* Planner-style tabs */}
      <div style={{
        display: "flex", gap: 4, background: C.surface, borderRadius: 12,
        padding: 4, marginBottom: 20, width: "fit-content", border: `1px solid ${C.border}`,
      }}>
        {[
          { key: "generate", label: "Generate", icon: "ti-sparkles" },
          { key: "saved", label: "Saved", icon: "ti-folder", count: saved.length },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer",
              fontSize: 14, fontWeight: 600, fontFamily: "inherit",
              background: tab === t.key ? C.primary : "transparent",
              color: tab === t.key ? "#fff" : C.muted,
            }}
          >
            <i className={`ti ${t.icon}`} style={{ fontSize: 15 }} />
            {t.label}
            {t.key === "saved" && saved.length > 0 && (
              <span style={{
                background: tab === t.key ? "rgba(255,255,255,0.25)" : C.tagBg,
                color: tab === t.key ? "#fff" : C.tagText,
                borderRadius: 99, fontSize: 11, padding: "1px 7px", fontWeight: 700,
              }}>
                {saved.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === "generate" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, animation: "ppFade 0.2s ease" }}>
          {/* Setup card — left blue accent like Planner */}
          <Card style={{ borderLeft: `4px solid ${C.primary}` }}>
            <SectionTitle
              icon={<i className="ti ti-building" />}
              title="Company & settings"
              subtitle="Choose who you’re preparing for, then generate a fresh practice set"
            />

            {/* Quick company pills */}
            {!useCustom && (
              <div style={{ marginBottom: 14 }}>
                <label style={labelStyle}>Popular companies</label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {QUICK_COMPANIES.map((name) => {
                    const active = company === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        onClick={() => pickQuick(name)}
                        style={{
                          padding: "8px 14px",
                          borderRadius: 20,
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: "inherit",
                          border: `2px solid ${active ? C.primary : C.border}`,
                          background: active ? C.primarySoft : C.surface,
                          color: active ? C.primaryDark : C.muted,
                        }}
                      >
                        {name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              marginBottom: 14,
            }}>
              <div>
                <label style={labelStyle}>Company</label>
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  {[[false, "List"], [true, "Custom"]].map(([v, label]) => (
                    <button
                      key={String(v)}
                      type="button"
                      onClick={() => setUseCustom(v)}
                      style={{
                        padding: "5px 12px",
                        borderRadius: 8,
                        cursor: "pointer",
                        fontSize: 11,
                        fontWeight: 700,
                        fontFamily: "inherit",
                        border: `1.5px solid ${useCustom === v ? C.primary : C.border}`,
                        background: useCustom === v ? C.primarySoft : C.bg,
                        color: useCustom === v ? C.primaryDark : C.muted,
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {useCustom ? (
                  <input
                    value={customCompany}
                    onChange={(e) => setCustomCompany(e.target.value)}
                    placeholder="Type any company name…"
                    style={fieldStyle}
                  />
                ) : (
                  <select value={company} onChange={(e) => setCompany(e.target.value)} style={fieldStyle}>
                    {[...new Set(companies.map((c) => c.category))].map((cat) => (
                      <optgroup key={cat} label={cat}>
                        {companies.filter((c) => c.category === cat).map((c) => (
                          <option key={c.name} value={c.name}>{c.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label style={labelStyle}>Difficulty</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[["mixed", "Mixed"], ["easy", "Easy"], ["medium", "Medium"], ["hard", "Hard"]].map(([d, label]) => {
                    const active = level === d;
                    const col = DIFF_COLORS[d];
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => setLevel(d)}
                        style={{
                          padding: "10px 8px",
                          borderRadius: 10,
                          cursor: "pointer",
                          fontSize: 12,
                          fontWeight: 700,
                          fontFamily: "inherit",
                          border: `2px solid ${active ? col : C.border}`,
                          background: active ? `${col}14` : C.surface,
                          color: active ? col : C.muted,
                        }}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label style={labelStyle}>Questions per section</label>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "10px 12px", borderRadius: 10,
                  background: C.primarySoft, border: `1px solid ${C.primary}22`,
                }}>
                  <button
                    type="button"
                    onClick={() => setPerSection((p) => Math.max(3, p - 1))}
                    style={{
                      width: 34, height: 34, borderRadius: 8,
                      border: `1.5px solid ${C.border}`, background: C.surface,
                      fontSize: 18, cursor: "pointer", color: C.primaryDark, fontFamily: "inherit",
                    }}
                  >−</button>
                  <div style={{ flex: 1, textAlign: "center" }}>
                    <div style={{ fontWeight: 900, fontSize: 22, color: C.primaryDark }}>{perSection}</div>
                    <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>per section</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPerSection((p) => Math.min(20, p + 1))}
                    style={{
                      width: 34, height: 34, borderRadius: 8,
                      border: `1.5px solid ${C.border}`, background: C.surface,
                      fontSize: 18, cursor: "pointer", color: C.primaryDark, fontFamily: "inherit",
                    }}
                  >+</button>
                </div>
              </div>
            </div>

            <label style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              fontSize: 12.5, color: C.muted, cursor: "pointer", marginBottom: 16,
            }}>
              <input
                type="checkbox"
                checked={refreshPattern}
                onChange={(e) => setRefreshPattern(e.target.checked)}
              />
              Force refresh live exam pattern
            </label>

            {error && (
              <div style={{
                padding: "10px 14px", borderRadius: 10, marginBottom: 12, fontSize: 13, fontWeight: 600,
                background: C.errorBg, color: C.error, border: `1px solid ${C.error}33`,
              }}>
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={handleGenerate}
              disabled={loading}
              style={{
                width: "100%",
                padding: "14px 18px",
                border: "none",
                borderRadius: 12,
                cursor: loading ? "not-allowed" : "pointer",
                fontWeight: 600,
                fontSize: 15,
                fontFamily: "inherit",
                color: "#fff",
                background: loading
                  ? C.muted
                  : C.primary,
                boxShadow: "none",
                opacity: loading ? 0.75 : 1,
              }}
            >
              {loading
                ? `Generating for ${activeCompany || "…"}…`
                : `Generate practice set${activeCompany ? ` · ${activeCompany}` : ""}`}
            </button>
          </Card>

          {loading && (
            <Card sx={{ textAlign: "center", py: 5, background: `${C.primarySoft} !important` }}>
              <div style={{
                width: 40, height: 40,
                border: `3px solid ${C.border}`,
                borderTopColor: C.primary,
                borderRadius: "50%",
                margin: "0 auto 14px",
                animation: "ppSpin 0.8s linear infinite",
              }} />
              <p style={{ margin: 0, fontWeight: 800, color: C.primaryDark }}>Building practice set…</p>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: C.muted }}>
                Looking up {activeCompany}'s pattern, then writing fresh questions
              </p>
            </Card>
          )}

          {!result && !loading && (
            <Card sx={{
              textAlign: "center", py: 5,
              background: "linear-gradient(135deg, #E8F0FF 0%, #F7F9FC 100%) !important",
              borderStyle: "dashed",
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14, margin: "0 auto 12px",
                background: C.surface, border: `1px solid ${C.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.primary, fontSize: 22,
              }}>
                <i className="ti ti-file-text" />
              </div>
              <p style={{ margin: 0, fontWeight: 800, color: C.text }}>Ready to generate</p>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: C.muted, maxWidth: 360, marginLeft: "auto", marginRight: "auto" }}>
                Pick a company above and hit Generate — your practice set will appear here.
              </p>
            </Card>
          )}

          {result && !loading && (
            <div style={{ animation: "ppFade 0.2s ease" }}>
              {/* Result stats — Daily Lock style */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: 14 }}>
                {[
                  { label: "Company", value: result.company, color: C.primary, soft: C.primarySoft },
                  { label: "Questions", value: totalQCount(result), color: C.success, soft: C.successBg },
                  { label: "Level", value: result.level, color: C.warning, soft: C.warningBg },
                ].map((s) => (
                  <Card
                    key={s.label}
                    style={{ padding: 0 }}
                    sx={{ p: "14px 16px !important", borderLeft: `4px solid ${s.color}`, background: `${s.soft} !important` }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                      {s.label}
                    </div>
                    <div style={{
                      marginTop: 4, fontSize: 18, fontWeight: 800, color: s.color,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>
                      {s.value}
                    </div>
                  </Card>
                ))}
              </div>

              <Card style={{ marginBottom: 14, borderLeft: `4px solid ${C.primary}` }}>
                <div style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  gap: 12, flexWrap: "wrap",
                }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>Practice set ready</div>
                    <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>
                      Review sections below — turn on answers when you want to check
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <Btn
                      small
                      variant="outline"
                      onClick={() => setShowAnswers((s) => !s)}
                      style={showAnswers ? {
                        borderColor: `${C.success}66`,
                        color: C.success,
                        background: C.successBg,
                      } : {}}
                    >
                      {showAnswers ? "Hide answers" : "Show answers"}
                    </Btn>
                    <Btn small variant="ghost" onClick={() => downloadBank(result)}>
                      Download
                    </Btn>
                  </div>
                </div>
              </Card>

              <PatternPanel pattern={result.pattern} isLive={meta?.isLive ?? result.pattern?.isLive} />

              {(result.sections || []).map((sec, si) => (
                <Collapsible key={si} title={sec.name} count={sec.mcq?.length || 0} defaultOpen={si === 0}>
                  {sec.topics?.length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                      {sec.topics.map((t, ti) => (
                        <Badge key={ti} bg={C.tagBg} color={C.tagText}>{t}</Badge>
                      ))}
                    </div>
                  )}
                  {(sec.mcq || []).map((q, i) => (
                    <MCQCard key={i} q={q} i={i} showAnswers={showAnswers} />
                  ))}
                </Collapsible>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "saved" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, animation: "ppFade 0.2s ease" }}>
          {savedLoading && (
            <p style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 32 }}>Loading saved sets…</p>
          )}

          {!savedLoading && !saved.length && (
            <Card sx={{
              textAlign: "center", py: 5,
              background: "linear-gradient(135deg, #E8F0FF 0%, #F7F9FC 100%) !important",
              borderStyle: "dashed",
            }}>
              <p style={{ margin: 0, fontWeight: 800, color: C.text }}>No saved sets yet</p>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: C.muted }}>
                Generate a practice set and it will show up here.
              </p>
            </Card>
          )}

          {!savedLoading && saved.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
              {saved.map((b) => {
                const selected = selectedBank?._id === b._id;
                return (
                  <div
                    key={b._id}
                    role="button"
                    tabIndex={0}
                    onClick={async () => {
                      if (selected) { setSelectedBank(null); return; }
                      try {
                        const r = await axios.get(`${BASE}/placement/${b._id}`, { headers: authH() });
                        setSelectedBank(r.data.data);
                        setShowAnswers(false);
                      } catch { /* ignore */ }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.click(); }}
                  >
                    <Card
                      style={{ padding: 0, cursor: "pointer", height: "100%" }}
                      sx={{
                        p: "16px !important",
                        borderLeft: `4px solid ${selected ? C.primary : C.border} !important`,
                        background: selected ? `${C.primarySoft} !important` : undefined,
                        transition: "border-color 0.15s, background 0.15s",
                        "&:hover": { borderLeftColor: `${C.primary} !important` },
                      }}
                    >
                      <div style={{ fontWeight: 800, fontSize: 15, color: C.text }}>{b.company}</div>
                      <div style={{ fontSize: 12, color: C.muted, marginTop: 4, marginBottom: 12 }}>
                        {b.level} · {totalQCount(b)} Q · {new Date(b.updatedAt).toLocaleDateString()}
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Badge bg={C.tagBg} color={C.tagText}>
                          {b.pattern?.isLive ? "Live" : "Saved"}
                        </Badge>
                        <Btn
                          small
                          variant="ghost"
                          onClick={(e) => { e.stopPropagation(); handleDelete(b._id); }}
                          style={{ color: C.error }}
                        >
                          Delete
                        </Btn>
                      </div>
                    </Card>
                  </div>
                );
              })}
            </div>
          )}

          {selectedBank && (
            <div>
              <Card style={{ marginBottom: 14, borderLeft: `4px solid ${C.primary}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 16, color: C.text }}>{selectedBank.company}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {selectedBank.level} · {totalQCount(selectedBank)} questions
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <Btn small variant="outline" onClick={() => setShowAnswers((s) => !s)}>
                      {showAnswers ? "Hide answers" : "Show answers"}
                    </Btn>
                    <Btn small variant="ghost" onClick={() => setSelectedBank(null)}>Close</Btn>
                  </div>
                </div>
              </Card>
              <PatternPanel pattern={selectedBank.pattern} isLive={selectedBank.pattern?.isLive} />
              {(selectedBank.sections || []).map((sec, si) => (
                <Collapsible key={si} title={sec.name} count={sec.mcq?.length || 0} defaultOpen={si === 0}>
                  {(sec.mcq || []).map((q, i) => (
                    <MCQCard key={i} q={q} i={i} showAnswers={showAnswers} />
                  ))}
                </Collapsible>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
